import {useEffect, useState} from 'react'

import {useFormik} from 'formik'
import {useNavigate} from 'react-router'
import * as Yup from 'yup'

import config from '../../config'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {signOut} from '../../redux/features/auth'

import useGuard from '../../../../commons/src/hooks/use-guard'
import {HttpStatusError} from '../../../../commons/src/libs/http-json'
import {sanitizeTextLineBreaks} from '../../../../commons/src/libs/messages'
import {formatDateTime} from '../../libs/formatter'
import * as partnerApi from '../../services/api/partner'
import {captureException, captureIgnoredException} from '../../services/logging'
import {goToOrderDetail} from '../../services/navigation'

import {ProviderReview} from '../../../../commons/src/types'

import editIcon from '../../../../commons/src/assets/icons/edit.svg'
import sendIcon from '../../../../commons/src/assets/icons/send.svg'

import ActionBar from '../../../../commons/src/components/action-bar'
import Button from '../../../../commons/src/components/button'
import DescriptionList from '../../../../commons/src/components/description-list'
import DescriptionListItem from '../../../../commons/src/components/description-list/DescriptionListItem'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import Image from '../../../../commons/src/components/image'
import ImageLightbox from '../../../../commons/src/components/image-lightbox'
import LoadingIndicator from '../../../../commons/src/components/loading-indicator/LoadingIndicator'
import MessageContentCard from '../../../../commons/src/components/message-content-card'
import ModalHandler from '../../../../commons/src/components/modal-handler'
import Newlines from '../../../../commons/src/components/newlines'
import Paragraph from '../../../../commons/src/components/paragraph'
import SplitLayout from '../../../../commons/src/components/split-layout/split-layout'
import Stack from '../../../../commons/src/components/stack'
import StarRating from '../../../../commons/src/components/star-rating'
import TextField from '../../../../commons/src/components/text-field'
import Container from '../../components/container'
import MessagesSplitLayout from '../../components/messages-split-layout/MessagesSplitLayout'
import MessagesSplitLayoutColumn from '../../components/messages-split-layout/MessagesSplitLayoutColumn'

import {Order, OrderDetailTab} from '../../order-types'
import SaveButtonPartial from '../save-button-partial'

class OrderReviewReplyError extends Error {
  constructor(message?: string) {
    super(message || `Order reply request failed`)
    this.name = 'OrderReviewReplyError'
  }
}

type Props = {
  order: Order
  isLoading: boolean
  orderReview?: ProviderReview
  onOrderReviewUpdate: (updatedReview: ProviderReview) => void
}

const OrderReviewPartial = ({order, isLoading, orderReview, onOrderReviewUpdate}: Props) => {
  const dispatch = useAppDispatch()
  const provider = useAppSelector(state => state.core.provider)

  const navigate = useNavigate()

  const [lightbox, setLightbox] = useState<{imageUrls: string[]; imageIndex: number}>()
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSendingReply, setIsSendingReply] = useState(false)
  const [showReplyForm, setShowReplyForm] = useState(false)

  useGuard(!!orderReview || isLoading, () =>
    goToOrderDetail(navigate, {orderId: order.orderId, activeTab: OrderDetailTab.DETAILS})
  )

  const formik = useFormik({
    initialValues: {
      replyMessage: '',
      formikGeneral: '',
    },
    validationSchema: Yup.object({
      replyMessage: Yup.string().max(
        config.orderMessageMaxLength,
        `Your message length exceeds the maximum of ${config.orderMessageMaxLength} chars.`
      ),
    }),
    onSubmit: async (values, {setFieldError}) => {
      if (values.replyMessage.trim().length === 0) {
        setFieldError('formikGeneral', 'Reply message cannot be empty.')
        return
      }

      setIsSendingReply(true)
      try {
        const newReviewMessage = values.replyMessage.trim()
        await partnerApi.patchOrderReviewReply(orderReview!.id, newReviewMessage)
        const updatedReview = await partnerApi.getOrderReview(order.orderId)
        onOrderReviewUpdate(updatedReview)
        setIsSubmitted(true)
        setShowReplyForm(false)
      } catch (error) {
        if (error && error instanceof HttpStatusError && error.response.status === 401) {
          captureIgnoredException(error)
          dispatch(signOut())
        } else {
          captureException(new OrderReviewReplyError((error as Error)?.message), 'default', {
            originalError: error,
            providerId: provider?.id,
            reply: values.replyMessage,
          })
          setFieldError('formikGeneral', 'Oops something went wrong. Please try again!')
        }
      } finally {
        setIsSendingReply(false)
      }
    },
  })

  useEffect(() => {
    if (showReplyForm && orderReview?.vendorReply) {
      formik.setValues({
        replyMessage: sanitizeTextLineBreaks(orderReview.vendorReply),
        formikGeneral: '',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showReplyForm])

  const getReviewDate = (review: ProviderReview) => {
    const created = formatDateTime(review.createdAt)
    const updated = formatDateTime(review.updatedAt)
    if (created !== updated) {
      return `${created} (last update: ${updated})`
    }
    return created
  }

  const renderLabel = (label: string) => (
    <Paragraph size="s" className="u-no-margin-bottom">
      {label}
    </Paragraph>
  )

  const renderReview = (review: ProviderReview) => (
    <Stack space="200">
      {/* Star ratings */}
      <Stack>
        <Stack space="60">
          <SplitLayout
            left={
              <Stack space="80">
                <ActionBar alignItems="bottom" space="300">
                  <Paragraph strong className="u-no-margin-bottom">
                    Total
                  </Paragraph>
                  <StarRating stars={review.total} suffix={`${review.total} out of 5`} size="l" />
                </ActionBar>
                <Paragraph minor size="xs" className="u-no-margin-bottom">
                  {getReviewDate(review)}
                </Paragraph>
              </Stack>
            }
            right={
              !review.vendorReply &&
              !showReplyForm && (
                <Button
                  hierarchy="secondary"
                  onClick={() => {
                    setShowReplyForm(true)
                  }}
                  disabled={isSendingReply}
                >
                  Reply
                </Button>
              )
            }
          />
        </Stack>
        <DescriptionList noRowGap>
          <DescriptionListItem label={renderLabel('Quality')}>
            <StarRating stars={review.quality} />
          </DescriptionListItem>
          <DescriptionListItem label={renderLabel('Lead time')}>
            <StarRating stars={review.leadTime} />
          </DescriptionListItem>
          <DescriptionListItem label={renderLabel('Value for money')}>
            <StarRating stars={review.valueForMoney} />
          </DescriptionListItem>
          <DescriptionListItem label={renderLabel('Communication')}>
            {review.communication ? (
              <StarRating stars={review.communication} />
            ) : (
              <Paragraph className="u-no-margin-bottom" minor size="xs">
                not rated
              </Paragraph>
            )}
          </DescriptionListItem>
        </DescriptionList>
      </Stack>
      {/* Comments */}
      {review.comments ? (
        <Paragraph size="s" className="u-no-margin-bottom">
          <Newlines text={review.comments} />
        </Paragraph>
      ) : (
        <Paragraph minor size="xs" className="u-no-margin-bottom">
          No additional comments
        </Paragraph>
      )}
      {/* Attachments */}
      {review.attachments.length > 0 && (
        <ModalHandler
          lightbox
          modal={({close}) => (
            <ImageLightbox
              onClose={() => {
                setLightbox(undefined)
                close()
              }}
              defaultImageIndex={lightbox?.imageIndex}
            >
              {(lightbox?.imageUrls ?? []).map(url => (
                <Image key={url} src={url} alt="" fit="contain" />
              ))}
            </ImageLightbox>
          )}
        >
          {({open}) => (
            <ActionBar>
              {review.attachments.map((attachment, index) => (
                <button
                  type="button"
                  key={attachment.id}
                  aria-label={attachment.name}
                  onClick={() => {
                    setLightbox({
                      imageUrls: review.attachments.map(a => a.url),
                      imageIndex: index,
                    })
                    open()
                  }}
                >
                  <Image
                    key={attachment.id}
                    height="70"
                    src={attachment.url}
                    alt={attachment.name}
                  />
                </button>
              ))}
            </ActionBar>
          )}
        </ModalHandler>
      )}
    </Stack>
  )

  const renderOrderReviewReply = (replyMessage: string) => (
    <Stack>
      <SplitLayout
        left={<Headline size="s" label="Your reply to the review" className="u-no-margin-bottom" />}
        right={
          <Button
            hierarchy="secondary"
            icon={<Icon source={editIcon} />}
            onClick={() => setShowReplyForm(true)}
          >
            Edit
          </Button>
        }
      />

      <Paragraph size="s" className="u-no-margin-bottom">
        <Newlines text={replyMessage} />
      </Paragraph>
    </Stack>
  )

  const renderReplyForm = () => (
    <MessageContentCard
      footer={
        <ActionBar align="right">
          <SaveButtonPartial
            formik={formik}
            isSubmitted={isSubmitted}
            icon={<Icon source={sendIcon} />}
            iconAlign="right"
            label="Send"
            doneLabel="Sent"
            submitOnEnter={false}
            disabled={isSendingReply}
          />
        </ActionBar>
      }
    >
      <Paragraph strong className="u-no-margin-bottom">
        Comment on the review. <br />
      </Paragraph>
      <Paragraph size="s" minor className="u-no-margin-bottom">
        <strong>Please note:</strong> Your comment will be visible to other customers on Craftcloud.
        Do not share private information.
      </Paragraph>
      <form onSubmit={formik.handleSubmit}>
        <TextField
          placeholder="Your reply to the customer's review"
          name="replyMessage"
          value={formik.values.replyMessage}
          maxLength={config.orderMessageMaxLength}
          appearance={formik.submitCount > 0 && !!formik.errors.replyMessage ? 'error' : undefined}
          onChange={(value, name) => {
            formik.setFieldValue(name as string, value)
            formik.setFieldError('formikGeneral', '')
            if (isSubmitted) {
              setIsSubmitted(false)
            }
          }}
          onBlur={formik.handleBlur}
          disabled={isSendingReply}
        />
      </form>
      {formik.errors.formikGeneral && (
        <Paragraph size="s" error>
          {formik.errors.formikGeneral}
        </Paragraph>
      )}
    </MessageContentCard>
  )

  return orderReview ? (
    <MessagesSplitLayout>
      <MessagesSplitLayoutColumn>{renderReview(orderReview)}</MessagesSplitLayoutColumn>
      <MessagesSplitLayoutColumn borderLeft={!showReplyForm}>
        <>
          {orderReview.vendorReply &&
            !showReplyForm &&
            renderOrderReviewReply(orderReview.vendorReply)}
          {showReplyForm && renderReplyForm()}
        </>
      </MessagesSplitLayoutColumn>
    </MessagesSplitLayout>
  ) : isLoading ? (
    <Container className="u-align-center">
      <Stack>
        <LoadingIndicator />
        <Paragraph minor className="u-no-margin-bottom">
          Loading order review
        </Paragraph>
      </Stack>
    </Container>
  ) : null
}

export default OrderReviewPartial
